/**
 * PWA (Progressive Web App) Components
 * - Offline indicator
 * - Install prompt
 * - Update available notification
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import {
  WifiOff,
  Wifi,
  Download,
  RefreshCw,
  X,
  Smartphone,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

// Hook to detect online/offline status
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online', { duration: 2000 });
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Some features may be limited.', {
        duration: 4000,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Hook to manage PWA install prompt
export const useInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      toast.success('String Sauce installed successfully!');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return false;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      setInstallPrompt(null);
      return true;
    }
    return false;
  }, [installPrompt]);

  return {
    canInstall: !!installPrompt && !isInstalled,
    isInstalled,
    promptInstall,
  };
};

// Offline Indicator Component
export const OfflineIndicator = () => {
  const isOnline = useOnlineStatus();

  return (
    <AnimatePresence>
      {!isOnline && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-black px-4 py-2 flex items-center justify-center gap-2"
          data-testid="offline-indicator"
        >
          <WifiOff className="w-4 h-4" />
          <span className="text-sm font-medium">
            You're offline. Some features may not work.
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// PWA Install Banner
export const InstallBanner = ({ onDismiss }) => {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('pwa-install-dismissed') === 'true';
  });

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
    onDismiss?.();
  };

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      handleDismiss();
    }
  };

  if (!canInstall || dismissed) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[380px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 overflow-hidden"
      data-testid="install-banner"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white mb-1">Install String Sauce</h3>
            <p className="text-sm text-zinc-400">
              Add to your home screen for faster access and offline mode.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-zinc-500 hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="flex gap-2 mt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="flex-1 text-zinc-400"
          >
            Not now
          </Button>
          <Button
            size="sm"
            onClick={handleInstall}
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Install
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Update Available Notification
export const UpdateNotification = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem('pwa-update-dismissed') === 'true';
  });

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    
    if ('serviceWorker' in navigator) {
      const hasController = !!navigator.serviceWorker.controller;
      
      if (!hasController) {
        return;
      }

      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (
                newWorker.state === 'installed' && 
                navigator.serviceWorker.controller &&
                registration.waiting
              ) {
                // Wait briefly — if the SW uses skipWaiting, the controllerchange
                // event will fire and we dismiss automatically. Only show the banner
                // if the SW is still waiting after 2 seconds (manual update needed).
                setTimeout(() => {
                  if (registration.waiting) {
                    setUpdateAvailable(true);
                  }
                }, 2000);
              }
            });
          }
        });

        if (registration.waiting && navigator.serviceWorker.controller) {
          if (registration.active && registration.waiting !== registration.active) {
            setTimeout(() => {
              if (registration.waiting) {
                setUpdateAvailable(true);
              }
            }, 2000);
          }
        }
      });

      // When the new SW takes control (after skipWaiting), dismiss the banner
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setUpdateAvailable(false);
        setDismissed(true);
        if (refreshing) return;
        refreshing = true;
      });
    }
  }, []);

  const handleUpdate = () => {
    // Tell waiting service worker to take control
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
    window.location.reload();
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('pwa-update-dismissed', 'true');
  };

  if (!updateAvailable || dismissed) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-4 right-4 bg-indigo-600 text-white px-4 py-3 rounded-xl shadow-2xl z-50 flex items-center gap-3"
      data-testid="update-notification"
    >
      <RefreshCw className="w-5 h-5" />
      <div>
        <p className="text-sm font-medium">Update available</p>
        <p className="text-xs text-indigo-200">Refresh to get the latest version</p>
      </div>
      <Button
        size="sm"
        variant="secondary"
        onClick={handleUpdate}
        className="bg-white text-indigo-600 hover:bg-indigo-50"
      >
        Refresh
      </Button>
      <button
        onClick={handleDismiss}
        className="text-indigo-200 hover:text-white p-1 ml-1"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

// Online status badge (for use in header/footer)
export const OnlineStatusBadge = ({ className = '' }) => {
  const isOnline = useOnlineStatus();

  return (
    <div
      className={`flex items-center gap-1.5 text-xs ${className}`}
      data-testid="online-status-badge"
    >
      {isOnline ? (
        <>
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-emerald-400">Online</span>
        </>
      ) : (
        <>
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-amber-400">Offline</span>
        </>
      )}
    </div>
  );
};

export default OfflineIndicator;
